import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Text,
  TextInput,
  Stack,
  Alert,
} from '@mantine/core';

import { usePostForm } from 'api/forms/hooks';
import {
  WizardStepActions,
  WizardStepContent,
  WizardStepProps,
  WizardStepRoot,
} from 'components/WizardModal';
import { useIsMdBreakpoint } from 'hooks';
import { PostBoardFormsRequestType } from 'api/forms/types';
import { DYNAMIC_URLS } from 'consts';

import { RadioBox } from './components/RadioBox';

export type StepSettingsContext = {
  name: string;
  signType: 'SIMPLE' | 'SMS' | 'ANY';
  active?: boolean;
};

export const StepSettingsForm: React.FC<
  WizardStepProps<StepSettingsContext>
> = ({ close, context, wizardContext, goToPrevStep, stepData }) => {
  const navigation = useNavigate();
  const isMdBreakpoint = useIsMdBreakpoint();
  const { t } = useTranslation();
  const [name, setName] = useState(context.name || '');
  const [signType, setSignType] = useState<'SIMPLE' | 'SMS' | 'ANY'>(
    context.signType || 'ANY',
  );
  const [active, setActive] = useState(context.active || false);

  const { mutateAsync, isLoading } = usePostForm();

  const currentContext = {
    name,
    signType,
    active,
  };

  useEffect(
    () => () => {
      stepData({
        name,
        signType,
        active,
      });
    },
    [stepData, name, signType, active],
  );

  const handleSubmit = async () => {
    const result = await mutateAsync({
      ...wizardContext,
      ...currentContext,
    } as unknown as PostBoardFormsRequestType['bodyParams']);

    if (result) {
      navigation(DYNAMIC_URLS.formEdit({ formId: result.id }));
    }
  };

  const backButton = (
    <Button variant="default" onClick={() => goToPrevStep(currentContext)}>
      {t('common:back')}
    </Button>
  );

  const sendButton = (
    <Button
      disabled={name === ''}
      color="green"
      onClick={handleSubmit}
      loading={isLoading}
    >
      {t('forms:newFormWizard.create')}
    </Button>
  );

  const cancelButton = (
    <Button variant="default" onClick={close}>
      {t('common:cancel')}
    </Button>
  );

  return (
    <WizardStepRoot>
      <WizardStepContent>
        <Stack spacing={32}>
          <Stack spacing="lg">
            <Text fz="lg" fw={600}>
              {t('forms:newFormWizard.configureFormGeneral')}
            </Text>

            <Stack spacing="sm">
              <Text fz="lg" fw={500}>
                {t('forms:newFormWizard.configureFormName')} *
              </Text>
              <TextInput
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('forms:newFormWizard.configureFormName')}
              />
            </Stack>

            <Stack spacing="sm">
              <Text>{t('forms:newFormWizard.configureFormSignatureType')}</Text>
              <RadioBox
                name="method"
                value="ANY"
                checked={signType === 'ANY'}
                onChange={() => null}
                onClick={() => setSignType('ANY')}
                label={
                  <Stack spacing={0}>
                    <Text fz="lg" fw={600}>
                      {t('forms:newFormWizard.SimpleSignature')}
                    </Text>
                    <Text fz="sm" fw={400}>
                      {t('forms:newFormWizard.SimpleSignatureDescription')}
                    </Text>
                  </Stack>
                }
              />
              <RadioBox
                name="method"
                value="sms"
                checked={signType === 'SMS'}
                onChange={() => null}
                onClick={() => setSignType('SMS')}
                label={
                  <Stack spacing={0}>
                    <Text fz="lg" fw={600}>
                      {t('forms:newFormWizard.SmsSignature')}
                    </Text>
                    <Text fz="sm" fw={400}>
                      {t('forms:newFormWizard.SmsSignatureDescription')}
                    </Text>
                  </Stack>
                }
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <Text fz="lg" fw={600}>
              {t('forms:newFormWizard.configureFormShare')}
            </Text>
            <Box>
              <Checkbox
                checked={active}
                onChange={e => setActive(e.target.checked)}
                label={t('forms:newFormWizard.configureFormShareCheckbox')}
              />
              <Alert
                color="blue"
                title={t('forms:newFormWizard.configureFormShareTitle')}
              >
                {t('forms:newFormWizard.configureFormShareDescription')}
              </Alert>
            </Box>
          </Box>
        </Stack>
      </WizardStepContent>
      <WizardStepActions>
        {isMdBreakpoint ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 8,
              justifyContent: 'space-between',
            }}
          >
            {backButton}
            <Box sx={{ display: 'flex', gap: 8 }}>
              {cancelButton}
              {sendButton}
            </Box>
          </Box>
        ) : (
          <Stack spacing="sm">
            {/* Different button order in mobile! */}
            {sendButton}
            {backButton}
            {cancelButton}
          </Stack>
        )}
      </WizardStepActions>
    </WizardStepRoot>
  );
};
